import { useEffect } from "react";
import { MiControlledCheckBox, MiDetailFields } from "@miplatform/components";
import { COST_CALCULATION_TYPE, INPUT_TYPES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { TransferList } from "@mibuildweb/components";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { useCategoryDetailTabViewModel } from "./useCategoryDetailTabViewModel";

export function CategoryDetailTab({ form, extraTabProps = {} }) {
  const { control, setValue, watch } = form;
  const { isReadOnly } = extraTabProps;

  const { fetchAttributeOptions, attributeOptions } =
    useCategoryDetailTabViewModel();

  const { attributes, displayOrder, itemDescriptionPattern } = watch();

  useEffect(() => {
    fetchAttributeOptions();
  }, []);

  const categoryDetailField = [
    {
      label: "Category Name",
      name: "name",
      formControl: control,
      required: true,
      width: "50%",
      disabled: isReadOnly,
    },
    {
      label: "Display Order",
      name: "displayOrder",
      fieldType: INPUT_TYPES.NUMERIC,
      allowNegative: false,
      formControl: control,
      placeholder: "Display Order",
      width: "50%",
      decimalScale: 0,
      value: displayOrder,
      disabled: isReadOnly,
      helperText:
        "Enter a number to set the category order in the Filter Items.",
      setValue: (value) => {
        setValue("displayOrder", value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      fieldType: INPUT_TYPES.SWITCH,
      name: "imageRequired",
      disabled: isReadOnly,
      label: (
        <InputLabel
          title="Image Required:"
          description="Enforce that items in this category include an image."
        />
      ),
      formControl: control,
      padding: 0,
      width: "100%",
    },
    {
      fieldType: INPUT_TYPES.SWITCH,
      name: "dropRequired",
      disabled: isReadOnly,
      label: (
        <InputLabel
          title="Drop Required:"
          description="Items in this category require labor or materials to complete the installation."
        />
      ),
      formControl: control,
      padding: 0,
      width: "100%",
    },
    {
      fieldType: INPUT_TYPES.RADIO_GROUP,
      name: "costCalculation",
      disabled: isReadOnly,
      label: (
        <InputLabel
          title="Cost Calculation:"
          description="Select how cost is calculated by default for this category."
        />
      ),
      formControl: control,
      width: "100%",
      options: [
        { label: "Cost Plus", value: COST_CALCULATION_TYPE.COST_PLUS },
        { label: "List Down", value: COST_CALCULATION_TYPE.LIST_DOWN },
      ],
    },
  ];

  const availableAttributes = attributeOptions.map((option) => ({
    ...option,
    key: option.id,
    title: option.name,
  }));

  const selectedAttributes = attributes
    ?.sort((a, b) => a.displayOrder - b.displayOrder)
    .map((attribute) => ({
      ...attribute,
      key: attribute.id,
      title: attribute.name,
    }));

  const removeAttributeFromPattern = (pattern, attributeName) => {
    const parts = pattern.match(/\{[^{}]*\}/g) || [];
    const filteredParts = parts.filter((part) => {
      const cleanedPart = part.replaceAll("{", "").replaceAll("}", "");
      const [label] = cleanedPart.split(":");
      return label !== attributeName;
    });
    return filteredParts.join("-");
  };

  const handleAddAttribute = (key) => {
    const selectedAttribute = attributeOptions.find((a) => a.id === key);
    if (!selectedAttribute) {
      return;
    }
    const updatedAttributes = [
      ...attributes,
      {
        ...selectedAttribute,
        isRequired: true,
        displayOrder: attributes.length,
      },
    ];
    if (updatedAttributes.length > 10) {
      toastService.error({ text: "Maximum of 10 attributes can be added." });
      return;
    }
    setValue("attributes", updatedAttributes, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const currentPattern = itemDescriptionPattern;
    const newPattern = currentPattern
      ? `${currentPattern}-{${selectedAttribute.name}}`
      : `{${selectedAttribute.name}}`;

    setValue("itemDescriptionPattern", newPattern, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleRemoveAttribute = (key) => {
    const updatedAttributes = attributes.filter((a) => a.id !== key);
    setValue("attributes", updatedAttributes, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const removedAttribute = attributes.find((a) => a.id === key);
    if (removedAttribute) {
      const currentPattern = itemDescriptionPattern;
      const updatedPattern = removeAttributeFromPattern(
        currentPattern,
        removedAttribute.name
      );
      setValue("itemDescriptionPattern", updatedPattern, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const customAction = (_, index) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiControlledCheckBox
        formControl={control}
        label="Optional"
        disabled={isReadOnly}
        value={!attributes[index].isRequired}
        handleChange={(e) => {
          const isRequired = !e.target.checked;
          setValue(`attributes[${index}].isRequired`, isRequired, {
            shouldValidate: true,
            shouldDirty: true,
          });

          const currentPattern = itemDescriptionPattern;
          const attrName = attributes[index].name;
          if (isRequired) {
            const newPattern = currentPattern
              ? `${currentPattern}-{${attrName}}`
              : `{${attrName}}`;
            setValue("itemDescriptionPattern", newPattern, {
              shouldValidate: true,
              shouldDirty: true,
            });
          } else {
            const updatedPattern = removeAttributeFromPattern(
              currentPattern,
              attrName
            );
            setValue("itemDescriptionPattern", updatedPattern, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }}
      />
    </Box>
  );

  return (
    <>
      <ConfigAccordionHeader title="Enter Category Details">
        <MiDetailFields
          detailFields={categoryDetailField}
          headerProps={{
            subtitle:
              "Name the category, set its display order in lists, and choose whether items in it must include an image.",
            variant: "text",
          }}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Attach Attributes">
        <TransferList
          availableItems={availableAttributes}
          selectedItems={selectedAttributes}
          onAddItem={handleAddAttribute}
          onRemoveItem={handleRemoveAttribute}
          availableTitle="Available Attributes"
          selectedTitle="Selected Attributes"
          customAction={customAction}
          addDisabled={selectedAttributes.length === 10}
          disabled={isReadOnly}
          showAvailableItemsSearch
        />
      </ConfigAccordionHeader>
    </>
  );
}

function InputLabel({ title, description }) {
  const theme = useTheme();
  return (
    <Box display="flex" gap={1}>
      <Typography fontWeight={theme.fontWeight.semiBold}>{title}</Typography>
      <Typography>{description}</Typography>
    </Box>
  );
}
